import { useMutation, useQueryClient } from '@tanstack/react-query';

import httpInstances from '@/api/axiosInstance';
import { pastExportsOptions } from '@/features/DataHub/api';
import { PAST_EXPORTS_REFETCH_DELAY_MS } from '@/features/DataHub/constants/pastExports';
import type { CsvWidgetExportRequestParams } from '@/features/DataHub/modules/Export/types/context.types';
import type { ExportItemType } from '@/features/DataHub/types/pastExports';
import { normalizeSourceBackedDataExportFrom } from '@/features/DataHub/utils/sourceBackedDataExport';
import useSnackbarNotifications from '@/hooks/useSnackbarNotifications';

type ExportResponse = {
  downloadUrl?: string;
  message?: string;
};

const normalizeCsvWidgetExportRequestParams = (
  params: CsvWidgetExportRequestParams
): CsvWidgetExportRequestParams => ({
  ...params,
  from: normalizeSourceBackedDataExportFrom(params.from, params.to),
  to: params.to ?? null
});

export const useCsvWidgetExportMutation = () => {
  const queryClient = useQueryClient();
  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbarNotifications();

  return useMutation({
    mutationFn: async (params: CsvWidgetExportRequestParams) => {
      const normalizedParams = normalizeCsvWidgetExportRequestParams(params);
      const response = await httpInstances.axiosGoInstance.post<ExportResponse>(
        'export',
        normalizedParams,
        {
          validateStatus: status => status === 200 || status === 202
        }
      );
      return response;
    },
    onMutate: async (params: CsvWidgetExportRequestParams) => {
      const normalizedParams = normalizeCsvWidgetExportRequestParams(params);
      await queryClient.cancelQueries({ queryKey: pastExportsOptions.lists() });

      const previousExports = queryClient.getQueryData<ExportItemType[]>(
        pastExportsOptions.lists()
      );

      if (normalizedParams.id) {
        queryClient.setQueryData<ExportItemType[]>(pastExportsOptions.lists(), old =>
          old?.map(item =>
            item.id === normalizedParams.id
              ? {
                  ...item,
                  exportStatus: 'ongoing',
                  savedAt: Date.now(),
                  lastExecutionTime: Date.now()
                }
              : item
          )
        );
      } else {
        const tempId = `temp-${Date.now()}`;
        const newExport: ExportItemType = {
          id: tempId,
          name: normalizedParams.name,
          exportType: normalizedParams.exportType,
          exportSubType: null,
          savedAt: Date.now(),
          lastExecutionTime: Date.now(),
          exportStatus: 'ongoing',
          exportStatusDetails: null,
          exportSettings: {
            from: normalizedParams.from ?? '',
            to: normalizedParams.to ?? null,
            timezone: '',
            dateFormat: '',
            includeMetadata: false
          },
          additionalParams: normalizedParams.additionalParams,
          sourceSettings: {
            source: normalizedParams.source,
            from: normalizedParams.from,
            to: normalizedParams.to,
            tabName: normalizedParams.tabName
          }
        };

        queryClient.setQueryData<ExportItemType[]>(pastExportsOptions.lists(), old =>
          old ? [newExport, ...old] : [newExport]
        );
      }

      return { previousExports };
    },
    onSuccess: (_data, params) => {
      const isRegeneration = Boolean(params.id);
      showSuccessSnackbar(
        isRegeneration ? 'Export completed!' : 'Export completed! View in Past Exports anytime',
        'trade-export'
      );

      setTimeout(() => {
        queryClient.refetchQueries({ queryKey: pastExportsOptions.lists() });
      }, PAST_EXPORTS_REFETCH_DELAY_MS);
    },
    onError: (_error, _params, context) => {
      if (context?.previousExports) {
        queryClient.setQueryData(pastExportsOptions.lists(), context.previousExports);
      }
      showErrorSnackbar(
        'Oops! Something went wrong. Please try again later.',
        3000,
        'trade-export-error'
      );
    }
  });
};
